import { useState, type JSX } from 'react';
import './style.scss';
import '../../style.scss';
import DataUrlHelper from '../../../../../../../../../../helpers/data-url.helper';
import { IMicromobilityLink, MicromobilityLinkType } from '../../../../../../../../../../models/labs/micromobility/micromobility-link.model';
import { TranslationService } from '../../../../../../../../../../services/translation.service';
import MicromobilityTransportTrip from '../../../../../../../../../Shared/JourneySearchResults/MicromobilityTransportTrip';
import greaterThanIcon from '../../../../../../../../../../../assets/icons/greater-than.svg';
import MicromobilityHelper from '../../../../../../../../helpers/micromobility.helper';
import { SanitizerService } from '../../../../../../../../../../services/sanitizer.service';
import { DistanceHelper } from '../../../../../../../../../../helpers/distance.helper';
import DeviceHelper, { DeviceType } from '../../../../../../../../../../helpers/device.helper';
import Spinner from '../../../../../../../../../Shared/Spinner';
import { AnalyticsService } from '../../../../../../../../../../services/analytics-service';

function MicromobilityLeg({
  micromobilityLink,
  onMicromobilityLinkSelected,
  onUpdateAvailableVehicles,
}: {
  micromobilityLink: IMicromobilityLink;
  onMicromobilityLinkSelected: (micromobilityLink: IMicromobilityLink) => void;
  onUpdateAvailableVehicles: () => void;
}) {
  const [isLoading, setIsLoading] = useState(false);

  function getOperatorMoreInformationText(): string {
    if (micromobilityLink.type === MicromobilityLinkType.stationBound) {
      return TranslationService.get('travelplanner.micromobility.styrochstall.moreinfo');
    }
    if (micromobilityLink.vehicle && micromobilityLink.vehicle.operatorName === 'Voi') {
      return TranslationService.get('travelplanner.micromobility.voi.moreinfo');
    }
    if (micromobilityLink.vehicle && micromobilityLink.vehicle.operatorName === 'Dott') {
      return TranslationService.get('travelplanner.micromobility.dott.moreinfo');
    }
    if (micromobilityLink.vehicle && micromobilityLink.vehicle.operatorName === 'Ryde') {
      return TranslationService.get('travelplanner.micromobility.ryde.moreinfo');
    }
    return '';
  }

  function getOtherAvailableVehiclesText(): string {
    const translationText = TranslationService.get('travelplanner.micromobility.furtheravailablevehicles');
    const numberOfOtherVehicles = MicromobilityHelper.getNumberOfOtherAvailableVehicles(micromobilityLink);
    if (numberOfOtherVehicles === 0) return TranslationService.get('travelplanner.micromobility.noavailablevehicles');
    return translationText.replace('#availableVehicles#', numberOfOtherVehicles.toString());
  }

  function getRentalUri(micromobilityLink: IMicromobilityLink, deviceType: DeviceType): string | undefined {
    if (micromobilityLink.type === MicromobilityLinkType.stationBound) {
      return deviceType === DeviceType.Android ? micromobilityLink.originStation?.rentalUriAndroid : micromobilityLink.originStation?.rentalUriIos;
    }
    return deviceType === DeviceType.Android ? micromobilityLink.vehicle?.rentalUriAndroid : micromobilityLink.vehicle?.rentalUriIos;
  }

  function createLinkHtml(micromobilityLink: IMicromobilityLink): JSX.Element | undefined {
    const deviceType = DeviceHelper.getDeviceType();
    if (![DeviceType.Android, DeviceType.iOS].includes(deviceType)) {
      return undefined;
    }
    const rentalUri = getRentalUri(micromobilityLink, deviceType);

    return rentalUri !== undefined ? (
      <a href={encodeURI(rentalUri)} rel="noopener noreferrer" className="d-flex align-items-center link--external">
        {TranslationService.get('travelplanner.micromobility.bookwith')}{' '}
        {micromobilityLink.type === MicromobilityLinkType.stationBound ? micromobilityLink.originStation?.operatorName : micromobilityLink.vehicle?.operatorName}
      </a>
    ) : undefined;
  }

  if (isLoading) return <Spinner />;

  return (
    <>
      <div className="mb-2">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          href="#"
          className="micromobility-leg text-decoration-none d-block btn--outline-internal"
          onClick={(e) => {
            e.preventDefault();
            onMicromobilityLinkSelected(micromobilityLink);
          }}
        >
          <span className="visually-hidden">{TranslationService.get('travelplanner.readmoreabouttripleg')}</span>
          <div className={`bg-gray p-3 rounded-top-2 d-flex flex-column flex-nowrap text-color-gray-900`}>
            <div className="d-flex mb-2">
              <MicromobilityTransportTrip className="" micromobilityLink={micromobilityLink} showWithVehicleCount={false} />
              <span className="align-self-center flex-grow-1">
                {MicromobilityHelper.getVehicleTypeName(micromobilityLink)} {DistanceHelper.format(micromobilityLink.distanceInMeters)}
              </span>
              <img src={DataUrlHelper.encode(greaterThanIcon)} alt="" className="flex-shrink-0 align-self-center d-print-none" />
            </div>
            {(micromobilityLink.vehicle && micromobilityLink.vehicle.vehicleAvailable === false) ||
              (!micromobilityLink.vehicle && micromobilityLink.allAvailableStations?.[0].availableVehicles === 0 && (
                <div className="mb-1 micromobility-search__warning-text">{TranslationService.get('travelplanner.micromobility.vehiclenolongeravailable')}</div>
              ))}
            <div className="mb-1">{TranslationService.get('travelplanner.micromobility.pricenotincluded')}</div>
            <div className="mb-1">{getOtherAvailableVehiclesText()}</div>
          </div>
        </a>
        <div className="d-flex justify-content-between flex-wrap border-top border-1 border-gray-200 bg-gray p-3 rounded-bottom-2">
          <div className="d-flex align-items-center">
            <img src={MicromobilityHelper.getOperatorIconSrc(micromobilityLink)} alt="" className="me-2" />
            {micromobilityLink.vehicle && (
              <>
                <img src={MicromobilityHelper.createRangeIconSrc(micromobilityLink.vehicle)} alt="" className="me-1" />
                <span>{MicromobilityHelper.createRangeText(micromobilityLink.vehicle)}</span>
              </>
            )}
          </div>
          <div className="d-flex align-items-center">
            {DeviceHelper.getDeviceType() === DeviceType.Desktop ? (
              <div className="micromobility-leg__more-information" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(getOperatorMoreInformationText()) }}></div>
            ) : (
              createLinkHtml(micromobilityLink)
            )}
          </div>
        </div>
      </div>
      <div className="mb-2">
        <button
          className="btn--as-link micromobility-leg__update-availability"
          onClick={async () => {
            setIsLoading(true);
            await onUpdateAvailableVehicles();
            setIsLoading(false);
            AnalyticsService.addEvent(AnalyticsService.LabsMicromobility, 'Uppdatera tillgänglighet för hyrcykel i detaljvy', micromobilityLink.vehicle?.operatorName ?? '');
          }}
        >
          {TranslationService.get('travelplanner.micromobility.updateavailability')}
        </button>
      </div>
    </>
  );
}

export default MicromobilityLeg;

